import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { Badge } from "../../../components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import { CreditCard, Loader2, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { useApi } from "../../../hooks/useApi";
import { useAuth } from "../../../contexts/AuthContext";
import { cn } from "../../../lib/utils";
import { normalizeSubscriptionPlans } from "../../../utils/subscriptionPlans";

const ACCENT = "#14b8a6";
const ACCENT_HOVER = "#0d9488";

function isApiSuccess(res) {
  return res?.status === "success" || res?.success === true;
}

function parseMoney(v) {
  if (v == null || v === "") return NaN;
  return Number(String(v).replace(/,/g, ""));
}

function formatInr(amount) {
  const n = typeof amount === "number" ? amount : parseMoney(amount);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

function durationLabel(years) {
  const y = Number(years);
  if (!Number.isFinite(y) || y <= 0) return "—";
  return `${y} yr`;
}

export default function SubscriptionPlansAdmin() {
  const { user } = useAuth();
  const { get, patch, delete: del } = useApi();
  const role = (user?.role || "").toLowerCase();
  const canMutate = role === "super_admin" || role === "superadmin";

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ discounted_price: "", tag: "", is_active: true });
  const [saving, setSaving] = useState(false);
  const [sheetDesktop, setSheetDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const apply = () => setSheetDesktop(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await get("/customer/subscription-plans");
      if (isApiSuccess(res) && Array.isArray(res.data)) {
        setPlans(normalizeSubscriptionPlans(res.data));
      } else {
        setPlans([]);
      }
    } catch (err) {
      toast.error(err?.message || "Could not load plans");
      setPlans([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [get]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPlans();
  };

  const activeCount = plans.filter((p) => p.is_active !== false).length;

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      discounted_price: String(parseMoney(plan.discounted_price) ?? ""),
      tag: plan.tag ?? "",
      is_active: plan.is_active !== false,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    const price = Number(form.discounted_price);
    if (!Number.isFinite(price) || price < 0) {
      toast.error("Enter a valid discounted price");
      return;
    }
    setSaving(true);
    try {
      const res = await patch(`/subscription-plans/${editing.id}`, {
        discounted_price: price,
        tag: form.tag.trim() || null,
        is_active: form.is_active,
      });
      if (isApiSuccess(res)) {
        toast.success(res?.message || "Plan updated");
        setEditOpen(false);
        setEditing(null);
        await fetchPlans();
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (err) {
      toast.error(err?.message || "Could not update plan");
    } finally {
      setSaving(false);
    }
  };

  const removePlan = async (plan) => {
    if (!plan?.id) return;
    if (!window.confirm(`Delete plan “${plan.name}”? This cannot be undone.`)) return;
    try {
      const res = await del(`/subscription-plans/${plan.id}`);
      if (isApiSuccess(res)) {
        toast.success(res?.message || "Plan deleted");
        await fetchPlans();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch (err) {
      toast.error(err?.message || "Could not delete plan");
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      {/* Sticky top bar — mobile friendly */}
      <header className="sticky top-0 z-20 border-b border-border/60 bg-background/95 px-4 py-4 backdrop-blur-md sm:px-6">
        <div className="mx-auto flex max-w-3xl items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-teal-500/15 text-teal-700 dark:text-teal-400">
            <CreditCard className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold leading-tight tracking-tight text-foreground sm:text-xl">Subscription plans</h1>
            <p className="mt-0.5 text-xs leading-snug text-muted-foreground sm:text-sm">
              {canMutate
                ? "Tap a plan to edit pricing, tag, or visibility."
                : "View only. Super admin can make changes."}
            </p>
            {!loading && plans.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full border border-border/80 bg-muted/40 px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {plans.length} plan{plans.length !== 1 ? "s" : ""}
                </span>
                <span className="inline-flex items-center rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300">
                  {activeCount} active
                </span>
              </div>
            ) : null}
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-xl border-1 border-muted-foreground/20"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            aria-label="Refresh plans"
          >
            <RefreshCw className={cn("h-4 w-4 text-foreground", refreshing && "animate-spin")} />
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl space-y-4 px-3 pb-28 pt-4 sm:space-y-5 sm:px-6 sm:pb-12 sm:pt-5">
        {loading && plans.length === 0 ? (
          <Card className="overflow-hidden rounded-2xl border-1 border-muted-foreground/20 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
              <Loader2 className="h-9 w-9 animate-spin text-teal-600 dark:text-teal-400" />
              <p className="text-sm font-medium">Loading plans…</p>
            </CardContent>
          </Card>
        ) : plans.length === 0 ? (
          <Card className="rounded-2xl border-dashed border-1 border-muted-foreground/20 bg-muted/20 shadow-none">
            <CardContent className="flex flex-col items-center gap-2 py-14 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                <CreditCard className="h-7 w-7 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">No plans yet</p>
              <p className="max-w-xs text-xs text-muted-foreground">The API returned no subscription plans. Pull to refresh or check the backend.</p>
              <Button type="button" variant="outline" size="sm" className="mt-2 rounded-xl" onClick={handleRefresh}>
                Try again
              </Button>
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-3 sm:space-y-4">
            {plans.map((plan) => (
              <li key={plan.id} className="mx-auto w-full max-w-2xl">
                <Card
                  className={cn(
                    "overflow-hidden rounded-2xl border-1 border-muted-foreground/20 shadow-sm transition-shadow",
                    "hover:shadow-md dark:hover:shadow-md",
                    plan.is_active === false && "opacity-90"
                  )}
                >
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="truncate text-base font-bold leading-tight text-foreground sm:text-lg">{plan.name}</h2>
                          <Badge
                            variant="secondary"
                            className={cn(
                              "shrink-0 rounded-md px-2 py-0 text-[10px] font-bold uppercase tracking-wide",
                              plan.is_active !== false
                                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300"
                                : "bg-muted text-muted-foreground"
                            )}
                          >
                            {plan.is_active !== false ? "Live" : "Off"}
                          </Badge>
                        </div>
                        {plan.tag ? (
                          <p className="mt-1.5 truncate text-xs font-medium text-teal-700 dark:text-teal-300" title={plan.tag}>
                            {plan.tag}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-end gap-x-3 gap-y-1 border-t border-border/50 pt-4">
                      <span className="text-2xl font-bold tabular-nums tracking-tight text-foreground sm:text-3xl">
                        {formatInr(plan.discounted_price)}
                      </span>
                      <span className="pb-0.5 text-sm text-muted-foreground line-through tabular-nums">
                        {formatInr(plan.original_price)}
                      </span>
                      {plan.discount_percentage ? (
                        <Badge
                          variant="outline"
                          className="mb-0.5 rounded-md border-teal-500/30 text-[11px] font-semibold text-teal-700 dark:border-teal-500/40 dark:bg-teal-500/10 dark:text-teal-300"
                        >
                          {plan.discount_percentage}
                        </Badge>
                      ) : null}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-lg bg-muted/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                        {durationLabel(plan.duration_years)}
                      </span>
                      {plan.per_year_price != null ? (
                        <span className="rounded-lg bg-muted/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground tabular-nums">
                          {formatInr(plan.per_year_price)}/yr
                        </span>
                      ) : null}
                    </div>

                    {canMutate ? (
                      <div className="mt-4 grid grid-cols-2 gap-2 sm:gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 rounded-xl border-2 border-border/80 font-semibold sm:h-10"
                          onClick={() => openEdit(plan)}
                        >
                          <Pencil className="mr-2 h-4 w-4 shrink-0" />
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 rounded-xl border-2 border-destructive/40 font-semibold text-destructive hover:bg-destructive/10 dark:border-red-500/40 dark:text-red-300 dark:hover:bg-red-500/15 sm:h-10"
                          onClick={() => removePlan(plan)}
                        >
                          <Trash2 className="mr-2 h-4 w-4 shrink-0" />
                          Delete
                        </Button>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent
          side={sheetDesktop ? "right" : "bottom"}
          className={cn(
            "flex flex-col gap-0 border-border/60 bg-background p-0",
            sheetDesktop ? "w-full sm:max-w-md" : "max-h-[92vh] rounded-t-3xl"
          )}
        >
          <SheetHeader className="space-y-1 border-b border-border/50 px-4 pb-4 pt-6 text-left sm:px-6">
            <SheetTitle className="text-xl font-bold">Edit plan</SheetTitle>
            <SheetDescription className="text-sm">{editing?.name}</SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="space-y-2">
              <Label htmlFor="discounted_price" className="text-sm font-medium">
                Discounted price (INR)
              </Label>
              <Input
                id="discounted_price"
                type="number"
                min={0}
                step="0.01"
                value={form.discounted_price}
                onChange={(e) => setForm((f) => ({ ...f, discounted_price: e.target.value }))}
                className="h-12 rounded-xl border-border/80 text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tag" className="text-sm font-medium">
                Tag
              </Label>
              <Input
                id="tag"
                placeholder="e.g. Best Value"
                value={form.tag}
                onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))}
                className="h-12 rounded-xl border-border/80 text-base"
              />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-2xl border border-border/60 bg-muted/30 px-4 py-3.5">
              <div>
                <Label htmlFor="is_active" className="cursor-pointer text-sm font-medium">
                  Plan active
                </Label>
                <p className="text-[11px] text-muted-foreground">Hidden from customers when off</p>
              </div>
              <Switch id="is_active" checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
            </div>
          </div>

          <SheetFooter className="mt-auto flex-col gap-2 border-t border-border/50 bg-background/95 p-4 sm:flex-row sm:px-6">
            <Button
              type="button"
              variant="outline"
              className="h-12 w-full rounded-xl sm:h-11 sm:flex-1"
              onClick={() => setEditOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="h-12 w-full rounded-xl font-semibold text-white sm:h-11 sm:flex-1"
              style={{ backgroundColor: ACCENT }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = ACCENT_HOVER;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = ACCENT;
              }}
              disabled={saving}
              onClick={saveEdit}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save changes"
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
